// eslint-disable-next-line import/no-extraneous-dependencies
import Vue from 'vue';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import {
    ABST_PROTEIN, PROTEIN_DB, SEARCH_TYPE, SEARCH_TYPE_BE_TO_FE,
} from '../constants';
import { configKeys } from '../constants/constantConfig';
import { createSettingConfig, SettingConfigTemplates } from '../setting-config';
import AlgorithmMixin from './algorithm';
import AlgorithmTypeMixin from './algorithm-type';

const mapProps = (props) => {
    // eslint-disable-next-line no-param-reassign
    props = Vue.observable(props);

    return mapValues(props, (v, k, obj) => ({
        get() {
            return obj[k];
        },
        set(val) {
            // eslint-disable-next-line no-param-reassign
            obj[k] = val;
        },
    }));
};

export const createStore = (props, ...mixins) => {
    const Store = {
        mixins: [AlgorithmTypeMixin, AlgorithmMixin, ...mixins],
        data() {
            const settingConfigs = mapValues(SettingConfigTemplates, (_, name) => createSettingConfig(name));
            return {
                // 控件注册的设置项
                settingConfigs,
                // 控件name与value的映射
                settingValues: {},
                isInitValues: false,
            };
        },
        computed: {
            ...mapProps(props),
            isMultiDbType() {
                return get(this.settingValues, 'dbType', []).length > 1;
            },
            transferredSearchType() {
                return SEARCH_TYPE_BE_TO_FE[this.searchType] || this.searchType;
            },
            configOptionsType() {
                const isProteinDb = get(this.settingValues, 'dbType', []).includes(PROTEIN_DB);
                const searchType = this.settingValues.sequenceType || ABST_PROTEIN;
                if (searchType === ABST_PROTEIN) {
                    if (isProteinDb) {
                        return configKeys.protTypeProtDbConfigs;
                    }
                    return configKeys.protTypeNucDbConfigs;
                }
                if (isProteinDb) {
                    return configKeys.nucTypeProtDbConfigs;
                }
                return configKeys.nucTypeNucDbConfigs;
            },
            isShort() {
                return this.searchType === SEARCH_TYPE.short;
            },
        },
        watch: {
            // 设置对象引用变动后重建 settingValues
            // !!不能deep监听!!
            searchSetting: {
                handler(to, from) {
                    if (to !== from) {
                        this.assignValues();
                    }
                },
                immediate: true,
            },
            // externalSetting内任意更新时，更新settingValues
            externalSetting: {
                handler() {
                    this.externalConfigNames.forEach((name) => {
                        this.readFieldValue(name);
                    });
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            assignValues() {
                this.isInitValues = true;
                this.settingValues = mapValues(this.settingConfigs, () => undefined);
                this.externalConfigNames.forEach((name) => {
                    this.readFieldValue(name);
                });
                Object.keys(this.settingConfigs).forEach((name) => {
                    this.readFieldValue(name);
                });
                setTimeout(() => {
                    this.isInitValues = false;
                    this.afterChangeType();
                }, 0);
            },
            getSettingConfig(name) {
                return this.settingConfigs[name];
            },
            isExternalField(name) {
                return this.externalConfigNames.includes(name);
            },
            // 重建设置时，初始化控件的值
            // 重建时机：注册控件时、searchSetting对象引用变动、externalSetting内任意更新时
            readFieldValue(name) {
                const config = this.getSettingConfig(name);
                if (config) {
                    const isExternal = this.isExternalField(name);
                    const getValue = config.getValue.bind(config);
                    const setting = isExternal ? this.externalSetting : this.searchSetting;
                    this.$set(this.settingValues, name, getValue(setting));
                }
            },
            // 将组件内现在的控件值更新到传进来的searchSetting上
            writeFieldValue(name) {
                const config = this.getSettingConfig(name);
                const isExternal = this.isExternalField(name);
                const val = this.settingValues[name];
                const setValue = config.setValue.bind(config);
                setValue(isExternal ? this.externalSetting : this.searchSetting, val);
                // console.log('Sync value', name, cloneDeep(val), isExternal ? this.externalSetting : this.searchSetting);
            },
            // xxx 不能打包图片？
            safeImagesUrl(name) {
                const imgs = {
                    similarityExplanation: this.images?.similarityExplanation,
                    ungappedExplanation: this.images?.ungappedExplanation,
                    globalAlignmentExplanation: this.images?.globalAlignmentExplanation,
                    globalAlignmentSimilarityExplanation: this.images?.globalAlignmentSimilarityExplanation,
                };
                return get(imgs, name);
            },
        },
    };

    return new Vue(Store);
};
